import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
} from '@mui/material';

// RFC 2606 reserved domains for documentation/examples
const RFC_2606_RESERVED_DOMAINS = [
  'example.com',
  'example.net',
  'example.org',
  'example.edu',
];

// RFC 6761 special-use TLDs (includes .example from RFC 2606)
const RFC_6761_RESERVED_TLDS = ['example', 'test', 'invalid', 'localhost'];

// Pattern matchers for special-use TLDs (anything.test, anything.invalid, etc.)
const SPECIAL_USE_TLD_PATTERNS = [
  /\.example$/i,
  /\.test$/i,
  /\.invalid$/i,
  /\.localhost$/i,
];

function isReservedDomain(domain: string | null): boolean {
  if (!domain) return false;
  const lowerDomain = domain.toLowerCase();

  // Check RFC 2606 exact matches (example.com, example.net, example.org)
  if (RFC_2606_RESERVED_DOMAINS.includes(lowerDomain)) return true;

  // Check RFC 6761 bare TLDs (example, test, invalid, localhost)
  if (RFC_6761_RESERVED_TLDS.includes(lowerDomain)) return true;

  // Check pattern matches for subdomains (anything.test, anything.invalid, etc.)
  return SPECIAL_USE_TLD_PATTERNS.some(pattern => pattern.test(lowerDomain));
}
import {
  CheckCircle as ValidIcon,
  Cancel as InvalidIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Dns as DnsIcon,
  Business as ProviderIcon,
  Rule as RuleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import type { ValidationResult } from '../api/types';

interface ValidationResultCardProps {
  result: ValidationResult;
  showDetails?: boolean;
}

export function ValidationResultCard({
  result,
  showDetails = true,
}: ValidationResultCardProps) {
  const [expanded, setExpanded] = useState(showDetails);

  const statusColor = result.isValid ? 'success' : 'error';
  const StatusIcon = result.isValid ? ValidIcon : InvalidIcon;

  // Parse email parts
  const atIndex = result.email.indexOf('@');
  const localPart = atIndex > 0 ? result.email.substring(0, atIndex) : null;
  const domain = atIndex > 0 ? result.email.substring(atIndex + 1) : null;

  // Check if domain is reserved (RFC 2606 or RFC 6761)
  const isReserved = isReservedDomain(domain);
  const hasMxRecords = result.dnsCheck?.hasMxRecords ?? false;
  const hasProvider = result.detectedProvider !== null;

  // Determine which RFC applies for the label
  const getReservedLabel = (): string => {
    if (!domain) return 'Domain Registered';
    const lowerDomain = domain.toLowerCase();
    if (RFC_2606_RESERVED_DOMAINS.includes(lowerDomain)) {
      return 'Reserved Domain (RFC 2606)';
    }
    if (RFC_6761_RESERVED_TLDS.includes(lowerDomain) ||
        SPECIAL_USE_TLD_PATTERNS.some(p => p.test(lowerDomain))) {
      return 'Special-Use Domain (RFC 6761)';
    }
    return 'Domain Registered';
  };

  const checks = [
    {
      label: 'Universal Rules',
      value: result.passedUniversalRules,
      icon: <RuleIcon />,
      secondary: result.passedUniversalRules ? null : 'Basic format requirements not met',
    },
    {
      label: isReserved ? getReservedLabel() : 'Domain Registered',
      value: result.dnsCheck?.domainExists ?? false,
      icon: isReserved ? <InfoIcon /> : <DnsIcon />,
      secondary: isReserved
        ? 'For documentation/testing — cannot receive real email'
        : (result.dnsCheck?.domainExists ? null : 'Domain does not exist'),
    },
    {
      label: 'MX Records Found',
      value: hasMxRecords,
      icon: <DnsIcon />,
      secondary: hasMxRecords ? null : 'Domain cannot receive email',
    },
    {
      label: 'Provider Detected',
      value: hasProvider,
      icon: <ProviderIcon />,
      secondary: hasProvider
        ? result.detectedProvider!.displayName
        : 'Generic validation rules applied',
    },
  ];

  const allFailures = [...result.universalFailures, ...result.providerFailures];

  return (
    <Card
      sx={{
        borderLeft: 4,
        borderColor: `${statusColor}.main`,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <StatusIcon color={statusColor} fontSize="large" />
            <Box>
              <Typography
                variant="h6"
                component="span"
                sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
              >
                {result.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={result.isValid ? 'Valid' : 'Invalid'}
                  color={statusColor}
                  size="small"
                />
                {result.detectedProvider && (
                  <Chip
                    label={result.detectedProvider.displayName}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {!result.isValid && allFailures.length > 0 && (
          <Box sx={{ mt: 1 }}>
            {allFailures.map((failure, index) => (
              <Typography
                key={index}
                color="error"
                variant="body2"
                sx={{ fontStyle: 'italic' }}
              >
                {failure.message}
              </Typography>
            ))}
          </Box>
        )}

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 2,
            }}
          >
            {localPart && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Local Part
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {localPart}
                </Typography>
              </Box>
            )}
            {domain && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Domain
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {domain}
                </Typography>
              </Box>
            )}
          </Box>

          {result.detectedProvider && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Provider
              </Typography>
              <Typography variant="body2">
                {result.detectedProvider.displayName}
              </Typography>
            </Box>
          )}

          {result.dnsCheck?.mxHosts && result.dnsCheck.mxHosts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                MX Hosts
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {result.dnsCheck.mxHosts.slice(0, 3).join(', ')}
                {result.dnsCheck.mxHosts.length > 3 && ` (+${result.dnsCheck.mxHosts.length - 3} more)`}
              </Typography>
            </Box>
          )}

          {/* Show punycode domain for internationalized domain names (IDN) */}
          {result.asciiDomain && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'secondary.lighter', borderRadius: 1, border: 1, borderColor: 'secondary.light' }}>
              <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 0.5 }}>
                🌐 Internationalized Domain (IDN)
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Unicode (Display)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {domain}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Punycode (DNS)
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {result.asciiDomain}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Mail servers use the punycode form for routing
              </Typography>
            </Box>
          )}

          {/* Show TLD metadata/story */}
          {result.tldInfo && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.lighter', borderRadius: 1, border: 1, borderColor: 'warning.light' }}>
              <Typography variant="subtitle2" color="warning.dark" sx={{ mb: 0.5 }}>
                🌍 Top-Level Domain
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                {result.tldInfo.flagEmoji && (
                  <Typography component="span" sx={{ fontSize: '5rem', lineHeight: 1 }}>
                    {result.tldInfo.flagEmoji}
                  </Typography>
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    .{result.tldInfo.tld.toLowerCase()}
                    {result.tldInfo.countryName && ` — ${result.tldInfo.countryName}`}
                    {result.tldInfo.brandName && ` — ${result.tldInfo.brandName}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {result.tldInfo.type === 'ccTLD' && 'Country-Code TLD'}
                    {result.tldInfo.type === 'gTLD' && 'Generic TLD'}
                    {result.tldInfo.type === 'sTLD' && 'Sponsored TLD'}
                    {result.tldInfo.type === 'grTLD' && 'Generic-Restricted TLD'}
                    {result.tldInfo.type === 'brand' && 'Brand TLD'}
                    {result.tldInfo.type === 'infrastructure' && 'Infrastructure TLD'}
                    {result.tldInfo.registrationModel === 'restricted' && ' • Restricted'}
                    {result.tldInfo.registrationModel === 'closed' && ' • Closed'}
                  </Typography>
                </Box>
              </Box>
              {result.tldInfo.isRepurposed && (
                <Typography variant="caption" color="warning.dark" sx={{ display: 'block', fontStyle: 'italic' }}>
                  🔄 Commonly used beyond its geographic origin
                </Typography>
              )}
              {result.tldInfo.notes && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {result.tldInfo.notes}
                </Typography>
              )}
              {result.tldInfo.sponsor && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Registry: {result.tldInfo.sponsor}
                </Typography>
              )}
            </Box>
          )}

          {/* Show canonical address when different from original (dots removed, subaddress stripped) */}
          {result.canonicalAddress && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'primary.lighter', borderRadius: 1, border: 1, borderColor: 'primary.light' }}>
              <Typography variant="subtitle2" color="primary.dark" sx={{ mb: 0.5 }}>
                📬 Routes To
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {result.canonicalAddress}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {result.subaddress
                  ? "The '+' and everything after it, plus dots, are ignored by this provider"
                  : 'Dots are ignored by this provider'}
              </Typography>
            </Box>
          )}

          {result.subaddress && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'info.lighter', borderRadius: 1, border: 1, borderColor: 'info.light' }}>
              <Typography variant="subtitle2" color="info.dark" sx={{ mb: 1 }}>
                📧 Subaddressed Email
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Base Username
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {result.subaddress.baseLocalPart}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tag
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    +{result.subaddress.subaddressTag}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Routes To
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {result.subaddress.routesTo}
                </Typography>
              </Box>
            </Box>
          )}

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Validation Checks
          </Typography>
          <List dense disablePadding>
            {checks.map((check) => (
              <ListItem key={check.label} disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {check.value ? (
                    <ValidIcon color="success" fontSize="small" />
                  ) : (
                    <InvalidIcon color="error" fontSize="small" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={check.label}
                  secondary={check.secondary}
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      </CardContent>
    </Card>
  );
}
